import { Box, Typography } from "@material-ui/core";
import { isNil } from "lodash-es";
import React from "react";
import { useLocation } from "react-router-dom";
import useSWR from "swr";
import {
  Activity,
  Contract,
  Property,
  TeamMemberRole,
  User,
} from "../api/models";

import ActivityLog from "../components/ActivityLog/ActivityLog";
import AlertWrapper from "../components/AlertWrapper";
import LegalReviewProgressBar from "../components/LegalReviewProgressBar";
import InviteModalReadOnly, {
  InviteModalType,
} from "../components/LegalReviewViewer/InviteModalReadOnly";
import InviteYourTeamMembersModal, {
  InviteYourTeamMembersModalType,
} from "../components/LegalReviewViewer/InviteYourTeamMembersModal";
import LegalReviewWhiteHeader from "../components/LegalReviewWhiteHeader";
import OnboardingWrapper from "../components/OnboardingWrapper";
import { useAlert } from "../contexts/Alert";
import { useMobile } from "../hooks/mobile";
import Hero from "../images/lawyer-hero.svg";
import { refreshContractTeamsAndRole } from "../stores/contract";
import { useSessionStore } from "../stores/Session";
import SignUpModal from "../components/Invited/SignUpModal";
import LoginModal from "../components/Invited/LoginModal";
import api from "../api/instance";
import Button, { ButtonType, ButtonSize } from "../components/common/Button";

interface ContentProps {
  contractId: number;
  recordId: number;
  property?: Property;
  canSelfAdd?: boolean;
  onSelfAdd?: () => void;
}

function Content({
  contractId,
  recordId,
  property,
  canSelfAdd,
  onSelfAdd,
}: ContentProps) {
  const isMobile = useMobile();

  return (
    <Box display="flex" flex={1} justifyContent="center" alignItems="center" height="80vh">
      <Box width={isMobile ? "90%" : "385px"} display="flex" flexDirection="column" alignItems="center">
        {!isMobile && (
          <LegalReviewProgressBar contractId={contractId} recordId={recordId} />
        )}

        <img src={Hero} style={{ marginTop: 16, marginBottom: 10 }} height={isMobile ? 200 : undefined} />

        {canSelfAdd ? (
          <Box mt="20px">
            <Button
              title="ADD ME TO THIS CONTRACT"
              type={ButtonType.primary}
              size={ButtonSize.medium}
              width="260px"
              onClick={onSelfAdd}
            />
          </Box>
        ) : (
          <>
            <Typography variant="h6" style={{ fontWeight: 700, textAlign: "center" }}>
              You don’t have access to this workspace
            </Typography>

            <Typography variant="body2" style={{ textAlign: "center", marginTop: 10 }}>
              Need access? Ask someone already invited.
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
}

export default function PayAndNotInvitedWorkspace() {
  const isMobile = useMobile();
  const location = useLocation();
  const { setSuccessMessage, setErrorMessage } = useAlert();

  const query = new URLSearchParams(location.search);

  // always convert to number | null (NOT undefined)
  const contractId: number | null = query.get("contractId")
    ? Number(query.get("contractId"))
    : null;

  const recordId: number | null = query.get("recordId")
    ? Number(query.get("recordId"))
    : null;

  const type = query.get("type");

  const { data: contract, mutate: contractMutate } = useSWR<Contract>(
    contractId ? `/view-contracts/${contractId}/` : null
  );

  const { data: property } = useSWR<Property>(
    contract?.property_id ? `/view-properties/${contract.property_id}/` : null
  );

  const { data: activitiesData, mutate: activitiesMutate } = useSWR<Activity[]>(
    contractId && recordId
      ? `/contract/${contractId}/record/${recordId}/activities/`
      : null
  );

  const { data: eligibilityData } = useSWR<{ can_self_add: boolean }>(
    contractId && recordId
      ? `/contract/${contractId}/record/${recordId}/self-add-eligibility/?type=${type}`
      : null
  );

  const canSelfAdd = eligibilityData?.can_self_add ?? false;

  const activities = activitiesData ?? [];

  const [loginModalOpen, setLoginModalOpen] = React.useState(false);
  const [inviteModalOpen, setInviteModalOpen] = React.useState(false);
  const [inviteYourTeamMembersOpen, setInviteYourTeamMembersOpen] = React.useState(false);

  const [signUpModalOpen, setSignUpModalOpen] = React.useState(false);

  const { refresh: refreshUser, refresh: refreshUserData } = useSessionStore();

  React.useEffect(() => {
    refreshUserData(false);
  }, []);

  const onSelfAdd = async () => {
    if (!contractId || !recordId) return;

    try {
      const res = await api.post(
        `/contract/${contractId}/record/${recordId}/self-add/`,
        { type }
      );

      setSuccessMessage(res.data.message);

      // safer navigation
      window.location.href = `/app/legal-review-viewer?contractId=${contractId}&recordId=${recordId}`;

      contractMutate();
      activitiesMutate();
    } catch (error) {
      const err = error as any;

      setErrorMessage(
        err?.response?.data?.message ||
          "We couldn't add you to this contract."
      );
    }
  };

  const onLoggedIn = () => {
    contractMutate();
    activitiesMutate();
  };

  const onSignedUp = () => {
    contractMutate();
    activitiesMutate();
  };

  return (
    <OnboardingWrapper onLoggedIn={onLoggedIn} onSignedUp={onSignedUp}>
      <AlertWrapper>
        <Box display="flex" flexDirection="column">
          <LegalReviewWhiteHeader
            property={property}
            onInviteClicked={() => setInviteModalOpen(true)}
          />

          {contractId && recordId && (
            <Box display="flex" flexDirection={isMobile ? "column" : "row"}>
              <Content
                contractId={contractId}
                recordId={recordId}
                property={property}
                canSelfAdd={canSelfAdd}
                onSelfAdd={onSelfAdd}
              />

              {!isMobile && (
                <Box mt="40px" width="353px">
                  <Typography variant="h6">Activity Log</Typography>
                  <ActivityLog activities={activities} />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </AlertWrapper>

      {/* LOGIN */}
      <LoginModal
        open={loginModalOpen}
        recordId={recordId}
        contractId={contractId}
        onLoggedIn={() => {
          setLoginModalOpen(false);
          refreshUser(false);
          contractMutate();
          activitiesMutate();
        }}
        onCancel={() => setLoginModalOpen(false)}
      />

      {/* SIGNUP */}
      <SignUpModal
        open={signUpModalOpen}
        onClose={() => setSignUpModalOpen(false)}
        contractId={contractId}
        token={query.get("token")}
        inviterName=""
        onSignedUp={() => {
          setSignUpModalOpen(false);
          refreshUserData();
        }}
        onLogin={() => {
          setSignUpModalOpen(false);
          setLoginModalOpen(true);
        }}
      />

      {/* INVITE */}
      <InviteModalReadOnly
        type={type === "buying" ? InviteModalType.buying : InviteModalType.selling}
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        contractId={contractId ?? 0}
        recordId={recordId ?? 0}
      />

      <InviteYourTeamMembersModal
        open={inviteYourTeamMembersOpen}
        onClose={() => setInviteYourTeamMembersOpen(false)}
        contractId={contractId ?? 0}
        recordId={recordId ?? 0}
        type={InviteYourTeamMembersModalType.default}
        userTeamMemberRole={null}
        onInvited={() => {}}
      />
    </OnboardingWrapper>
  );
}
